/**
 * Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { type FunctionDeclaration, SchemaType } from "@google/generative-ai";
import { useEffect, memo } from "react";
// import vegaEmbed from "vega-embed";
import { useLiveAPIContext } from "../../contexts/LiveAPIContext";
import { ToolCall } from "../../multimodal-live-types";

const declaration: FunctionDeclaration = {
  name: "verify_kyc",
  description: "Verifies name displayed from the Aadhaar card",
  parameters: {
    type: SchemaType.OBJECT,
    properties: {
      name: {
        type: SchemaType.STRING,
        description:
          "Name displayed on the front of the Aadhaar card (e.g. Harsha)",
      },
    },
    required: ["name"],
  },
};

function AltairComponent({expectedName}: {expectedName: string}) {
  // const [theString, setString] = useState<string>("");
  const { client, setConfig } = useLiveAPIContext();
  console.log("expectedName", expectedName);

  useEffect(() => {
    setConfig({
      model: "models/gemini-2.5-flash",
      generationConfig: {
        responseModalities: "audio",
        speechConfig: {
          voiceConfig: { prebuiltVoiceConfig: { voiceName: "Aoede" } },
        },
      },
      systemInstruction: {
        parts: [
          {
            text: 'You are KYC validator from Harsha Bank. Tell the user to hold the card in front of the camera and say "KYC verification". Call the function only after you clearly read the name from the card, otherwise say "I am sorry, I could not read the name from the card". Dont assume names unless it is clear.',
          },
        ],
      },
      tools: [
        // there is a free-tier quota for search
        { googleSearch: {} },
        { functionDeclarations: [declaration] },
      ],
    });
  }, [setConfig]);

  useEffect(() => {
    const onToolCall = (toolCall: ToolCall) => {
      console.log(`got toolcall`, toolCall);
      const fc = toolCall.functionCalls.find(
        (fc) => fc.name === declaration.name,
      );
      if (fc) {
        // const str = (fc.args as any).name;
        // setString(str);
      }
      // send data for the response of your tool call
      // in this case Im just saying it was successful
      if (toolCall.functionCalls.length) {
        console.log("sending response", toolCall.functionCalls);
        setTimeout(
          () =>
            client.sendToolResponse({
              functionResponses: toolCall.functionCalls.map((fc) => ({
                response: { output: { sucess: false, message: fc.args && typeof fc.args === 'object' && 'name' in fc.args && fc.args.name === expectedName ? "success" : "name not matching, name should be " + expectedName } },
                id: fc.id,
              })),
            }),
          20,
        );
      }
    };
    client.on("toolcall", onToolCall);
    return () => {
      client.off("toolcall", onToolCall);
    };
  }, [client, expectedName]);

  // const embedRef = useRef<HTMLDivElement>(null);
  return <></>
  // useEffect(() => {
  //   if (embedRef.current && theString) {
  //     vegaEmbed(embedRef.current, theString);
  //   }
  // }, [embedRef, theString]);
  // return <div className="vega-embed" ref={embedRef} />;
}

export const Altair = memo(AltairComponent);
